/**
 * Universal Auto-Capitalize Utility
 * Automatically converts all text to UPPERCASE in real-time
 */

let isGlobalAutoCapitalizeInitialized = false;

/**
 * Global input listener that ensures all typed/pasted text is converted
 * to real UPPERCASE in DOM and React state in real-time, even when CAPSLOCK is OFF.
 */
export const initGlobalAutoCapitalize = () => {
  if (typeof window === 'undefined' || isGlobalAutoCapitalizeInitialized) return;
  isGlobalAutoCapitalizeInitialized = true;

  const EXCLUDED_TYPES = new Set([
    'password', 'email', 'url', 'number', 'date', 'time',
    'datetime-local', 'month', 'week', 'file', 'checkbox', 'radio', 'range', 'color'
  ]);

  const handleInput = (e) => {
    if (e._isAutoCapitalized) return;
    const target = e.target;
    if (!target || (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA')) return;

    const type = (target.getAttribute('type') || 'text').toLowerCase();
    if (EXCLUDED_TYPES.has(type)) return;
    if (target.classList?.contains('no-uppercase')) return;

    const val = target.value;
    if (typeof val === 'string' && val) {
      const upper = val.toUpperCase();
      if (val !== upper) {
        const start = target.selectionStart;
        const end = target.selectionEnd;

        const proto = target.tagName === 'INPUT'
          ? window.HTMLInputElement.prototype
          : window.HTMLTextAreaElement.prototype;
        const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;

        if (nativeSetter) {
          nativeSetter.call(target, upper);
        } else {
          target.value = upper;
        }

        if (start !== null && end !== null) {
          try {
            target.setSelectionRange(start, end);
          } catch (err) {}
        }

        const ev = new Event('input', { bubbles: true });
        ev._isAutoCapitalized = true;
        target.dispatchEvent(ev);
      }
    }
  };

  document.addEventListener('input', handleInput, true);
};

/**
 * Auto-capitalize event handler
 * Usage: <Input onChange={(e) => handleAutoCapitalize(e, (val) => setFieldValue(val))} />
 */
export const handleAutoCapitalize = (e, callback) => {
  const upperValue = e.target.value.toUpperCase();
  
  // Update the input value immediately
  e.target.value = upperValue;
  
  // Call the callback with uppercase value
  if (callback) {
    callback(upperValue);
  }
  
  return upperValue;
};

/**
 * Auto-capitalize input component wrapper
 * Usage: <AutoCapitalizeInput value={name} onChange={setName} />
 */
export const AutoCapitalizeInput = ({ value, onChange, ...props }) => {
  const handleChange = (e) => {
    const upperValue = e.target.value.toUpperCase();
    onChange(upperValue);
  };
  
  return (
    <input
      {...props}
      value={value}
      onChange={handleChange}
      style={{ textTransform: 'uppercase', ...props.style }}
    />
  );
};

/**
 * Apply auto-capitalize to existing Input component
 */
export const withAutoCapitalize = (InputComponent) => {
  return ({ value, onChange, ...props }) => {
    const handleChange = (e) => {
      const upperValue = e.target.value.toUpperCase();
      onChange(upperValue);
    };
    
    return (
      <InputComponent
        {...props}
        value={value}
        onChange={handleChange}
        style={{ textTransform: 'uppercase' }}
      />
    );
  };
};

/**
 * CSS class for auto-capitalize
 * Usage: <Input className="uppercase" />
 */
export const AUTO_CAPITALIZE_CLASS = 'uppercase';

/**
 * Global CSS to apply to all text inputs (optional)
 */
export const GLOBAL_UPPERCASE_CSS = `
  input[type="text"]:not(.no-uppercase),
  input:not([type]):not(.no-uppercase),
  textarea:not(.no-uppercase) {
    text-transform: uppercase !important;
  }
  
  /* Exclude specific inputs */
  input[type="email"],
  input[type="url"],
  input[type="password"],
  input[type="number"],
  input[type="date"],
  input.no-uppercase {
    text-transform: none !important;
  }
`;

/**
 * Apply uppercase to a string
 */
export const toUpperCase = (str) => {
  return str ? String(str).toUpperCase() : '';
};

/**
 * Capitalize state setter wrapper
 */
export const useUpperCaseState = (initialValue = '') => {
  const [value, setValue] = React.useState(toUpperCase(initialValue));
  
  const setUpperValue = (newValue) => {
    setValue(toUpperCase(newValue));
  };
  
  return [value, setUpperValue];
};
